/* ==========================================================================
   Elfa Sale Landing Page — behaviour
   Soko & Co  |  loaded by sections/elfa-lp-css.liquid (defer)
   No dependencies. Everything is namespaced and guarded so it cannot throw
   on pages where the markup is absent (e.g. the theme editor mid-render).
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ------------------------------------------------------------- sliders */
  function initSlider(slider) {
    if (slider.dataset.elfaSliderReady === '1') return;
    slider.dataset.elfaSliderReady = '1';

    var track = slider.querySelector('[data-elfa-track]');
    if (!track) return;
    var prev = slider.querySelector('[data-elfa-prev]');
    var next = slider.querySelector('[data-elfa-next]');

    function step() {
      var first = track.firstElementChild;
      if (!first) return track.clientWidth * 0.8;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
      var card = first.getBoundingClientRect().width + gap;
      // Advance by whole cards, but never less than one.
      return Math.max(card, Math.floor(track.clientWidth / card) * card);
    }

    function sync() {
      if (!prev || !next) return;
      var max = track.scrollWidth - track.clientWidth - 2;
      var scrollable = max > 2;
      prev.disabled = !scrollable || track.scrollLeft <= 2;
      next.disabled = !scrollable || track.scrollLeft >= max;
    }

    function go(dir) {
      track.scrollBy({ left: dir * step(), behavior: REDUCED ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { sync(); ticking = false; });
    }, { passive: true });

    window.addEventListener('resize', sync, { passive: true });
    if ('ResizeObserver' in window) { new ResizeObserver(sync).observe(track); }
    sync();
  }

  /* ---------------------------------------------------------------- tabs */
  function initTabs(group) {
    if (group.dataset.elfaTabsReady === '1') return;
    group.dataset.elfaTabsReady = '1';

    var tabs = all('[role="tab"]', group);
    if (!tabs.length) return;

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !on;
          // Sliders inside a hidden panel measure 0 — refresh once revealed.
          if (on) all('[data-elfa-slider]', panel).forEach(function (s) {
            s.dataset.elfaSliderReady = '';
            initSlider(s);
          });
        }
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab); });
      tab.addEventListener('keydown', function (e) {
        var k = e.key, n = null;
        if (k === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        else if (k === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (k === 'Home') n = tabs[0];
        else if (k === 'End') n = tabs[tabs.length - 1];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });

    var initial = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
    select(initial);
  }

  /* ----------------------------------------------------------- countdown */
  function initCountdown(el) {
    if (el.dataset.elfaCountReady === '1') return;
    el.dataset.elfaCountReady = '1';

    var end = Date.parse(el.dataset.elfaCountdown);
    if (isNaN(end)) { el.hidden = true; return; }

    var out = {
      d: el.querySelector('[data-unit="d"]'),
      h: el.querySelector('[data-unit="h"]'),
      m: el.querySelector('[data-unit="m"]'),
      s: el.querySelector('[data-unit="s"]')
    };
    var pad = function (n) { return n < 10 ? '0' + n : String(n); };

    function tick() {
      var left = end - Date.now();
      if (left <= 0) { el.hidden = true; return; }
      var s = Math.floor(left / 1000);
      if (out.d) out.d.textContent = Math.floor(s / 86400);
      if (out.h) out.h.textContent = pad(Math.floor(s % 86400 / 3600));
      if (out.m) out.m.textContent = pad(Math.floor(s % 3600 / 60));
      if (out.s) out.s.textContent = pad(s % 60);
      window.setTimeout(tick, 1000);
    }
    tick();
  }

  /* --------------------------------------------------- sticky CTA + nav */
  function initSticky() {
    var bar = document.querySelector('[data-elfa-sticky]');
    var hero = document.querySelector('[data-elfa-hero]');
    if (!bar) return;

    if (!hero || !('IntersectionObserver' in window)) { bar.classList.add('is-on'); return; }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { bar.classList.toggle('is-on', !e.isIntersecting); });
    }, { rootMargin: '-120px 0px 0px 0px' }).observe(hero);
  }

  function initScrollSpy() {
    var nav = document.querySelector('[data-elfa-nav]');
    if (!nav || !('IntersectionObserver' in window)) return;

    var links = all('a[href^="#"]', nav);
    var map = {};
    var targets = [];

    links.forEach(function (a) {
      var id = a.getAttribute('href').slice(1);
      var t = id && document.getElementById(id);
      if (t) { map[id] = a; targets.push(t); }
    });
    if (!targets.length) return;

    var visible = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { visible[e.target.id] = e.isIntersecting ? e.intersectionRatio : 0; });

      var bestId = null, bestRatio = 0;
      targets.forEach(function (t) {
        var r = visible[t.id] || 0;
        if (r > bestRatio) { bestRatio = r; bestId = t.id; }
      });

      links.forEach(function (a) { a.classList.remove('is-active'); });
      if (bestId && map[bestId]) {
        map[bestId].classList.add('is-active');
        // keep the active chip in view on the horizontal nav
        var scroller = nav.querySelector('[data-elfa-navscroll]');
        if (scroller && scroller.scrollWidth > scroller.clientWidth) {
          var a = map[bestId];
          var target = a.offsetLeft - (scroller.clientWidth - a.offsetWidth) / 2;
          scroller.scrollTo({ left: Math.max(0, target), behavior: REDUCED ? 'auto' : 'smooth' });
        }
      }
    }, { rootMargin: '-96px 0px -55% 0px', threshold: [0, 0.15, 0.35, 0.6, 1] });

    targets.forEach(function (t) { io.observe(t); });
  }

  /* -------------------------------------------------------- scroll reveal */
  function initReveal(root) {
    var items = all('.elfa-rise', root);
    if (!items.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(function (i) { i.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    items.forEach(function (i) { io.observe(i); });
  }

  /* ------------------------------------------------------------- FAQ (one open) */
  function initFaq(group) {
    if (group.dataset.elfaFaqReady === '1') return;
    group.dataset.elfaFaqReady = '1';
    if (group.dataset.elfaSingle !== 'true') return;

    var items = all('details', group);
    items.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        items.forEach(function (o) { if (o !== d) o.open = false; });
      });
    });
  }

  /* ---------------------------------------------------------------- boot */
  function boot(root) {
    /* Tells the inline watchdog in elfa-lp-css.liquid that the reveal
       animations are safe to keep. Without this the watchdog removes the
       .elfa-anim flag and everything falls back to plain visible content. */
    window.__elfaLPReady = true;
    root = root || document;
    all('[data-elfa-slider]', root).forEach(initSlider);
    all('[data-elfa-tabs]', root).forEach(initTabs);
    all('[data-elfa-countdown]', root).forEach(initCountdown);
    all('[data-elfa-faq]', root).forEach(initFaq);
    initReveal(root);
    initSticky();
    initScrollSpy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Shopify theme editor: re-init a section after it is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
